import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import withAuth from '../../auth';
import { API_URL, WHITELABEL_ID } from '../../constants';
import { AuthContext } from '../AuthContext';
import Spinner from 'react-bootstrap/Spinner';

const InvoiceDetails = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const { token, setToken } = useContext(AuthContext);

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [isMarkPaidSubmitted, setIsMarkPaidSubmitted] = useState(false);

    const [paidMethod, setPaidMethod] = useState('EFT');
    const [paidReference, setPaidReference] = useState('');

    useEffect(() => {
        fetchInvoice();
    }, [invoiceId]);

    const fetchInvoice = async () => {
        setLoading(true);

        try {
            const response = await axios.get(
                `${API_URL}/api/wl/${WHITELABEL_ID}/manager/invoices/${invoiceId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setInvoice(response.data.data);
        } catch (error) {
            console.error('Error fetching invoice details', error);

            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            } else {
                setError('Failed to load invoice.');
            }
        } finally {
            setLoading(false);
        }
    };

    const openInvoicePdf = async () => {
        if (invoice?.pdf_url) {
            window.open(invoice.pdf_url, '_blank');
            return;
        }

        try {
            const response = await axios.get(
                `${API_URL}/api/wl/${WHITELABEL_ID}/manager/invoices/${invoice.id}/pdf`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data?.url) {
                window.open(response.data.url, '_blank');
            } else {
                alert('Invoice PDF not found.');
            }
        } catch (error) {
            console.error('Failed to open invoice PDF', error);
            alert('Failed to fetch invoice PDF.');
        }
    };

    const openReceiptPdf = () => {
        if (invoice?.receipt?.pdf_url) {
            window.open(invoice.receipt.pdf_url, '_blank');
            return;
        }

        alert('Receipt PDF not found.');
    };

    const markInvoicePaid = async () => {
        if (!window.confirm('Mark this invoice as paid?')) return;

        setIsMarkPaidSubmitted(true);
        setSuccess('');
        setError('');

        try {
            const response = await axios.post(
                `${API_URL}/api/wl/${WHITELABEL_ID}/manager/invoices/${invoice.id}/mark-paid`,
                {
                    paid_method: paidMethod,
                    paid_reference: paidReference || null,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSuccess(response.data.message || 'Invoice marked as paid.');
            await fetchInvoice();
        } catch (error) {
            console.error('Failed to mark invoice paid', error);
            setError(
                error?.response?.data?.message || 'Failed to mark invoice as paid.'
            );
        } finally {
            setIsMarkPaidSubmitted(false);
        }
    };

    const statusColors = {
        open: '#FFF4CC',
        paid: '#D1F2CF',
        void: '#FFD6D6',
    };

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: '80vh' }}
            >
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return <div className="container mt-3">Invoice not found.</div>;
    }

    return (
        <div className="container-fluid mt-3">
            <h2 className="text-center">Invoice Details #{invoice.invoice_number}</h2>

            {success && <div className="alert alert-success">{success}</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="row">
                <div className="col-md-6">
                    <div className="card mb-3">
                        <div className="card-body text-center">
                            <h5
                                className="p-2"
                                style={{
                                    backgroundColor: statusColors[invoice.status] || 'transparent',
                                }}
                            >
                                Status: {invoice.status}
                            </h5>

                            <p>
                                <strong>Invoice #:</strong> {invoice.invoice_number}
                                <br />
                                <strong>Issue Date:</strong>{' '}
                                {invoice.issue_date
                                    ? moment(invoice.issue_date).format('YYYY-MM-DD')
                                    : '—'}
                                <br />
                                <strong>Due Date:</strong>{' '}
                                {invoice.due_date
                                    ? moment(invoice.due_date).format('YYYY-MM-DD')
                                    : '—'}
                                <br />
                                <strong>Billing Period:</strong>{' '}
                                {invoice.billing_period_start && invoice.billing_period_end
                                    ? `${moment(invoice.billing_period_start).format(
                                        'YYYY-MM-DD'
                                    )} → ${moment(invoice.billing_period_end).format('YYYY-MM-DD')}`
                                    : '—'}
                                <br />
                                <strong>Currency:</strong> {invoice.currency}
                                <br />
                                <strong>Total:</strong> {invoice.total}
                            </p>

                            {invoice.user && (
                                <p>
                                    <strong>Customer:</strong>{' '}
                                    <Link to={`/users/${invoice.user.id}`}>
                                        {invoice.user.id} {invoice.user.full_name}
                                    </Link>
                                </p>
                            )}

                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                                <button className="btn btn-primary mr-2" onClick={openInvoicePdf}>
                                    Download Invoice
                                </button>

                                {invoice.receipt?.pdf_url && (
                                    <button className="btn btn-outline-success" onClick={openReceiptPdf}>
                                        Download Receipt
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {invoice.status !== 'paid' && invoice.status !== 'void' && (
                        <div className="card mb-3">
                            <div className="card-body">
                                <h5 className="text-center">Mark Invoice Paid</h5>

                                <div className="mb-3">
                                    <label className="form-label">Payment Method</label>
                                    <select
                                        className="form-control"
                                        value={paidMethod}
                                        onChange={(e) => setPaidMethod(e.target.value)}
                                    >
                                        <option value="EFT">EFT</option>
                                        <option value="Wire">Wire</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Cheque">Cheque</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Reference</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={paidReference}
                                        onChange={(e) => setPaidReference(e.target.value)}
                                        placeholder="Optional reference"
                                    />
                                </div>

                                <button
                                    className="btn btn-success"
                                    onClick={markInvoicePaid}
                                    disabled={isMarkPaidSubmitted}
                                >
                                    {isMarkPaidSubmitted ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            &nbsp;Marking Paid...
                                        </>
                                    ) : (
                                        'Mark as Paid'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {invoice.status === 'paid' && (
                        <div className="card mb-3">
                            <div className="card-body">
                                <h5 className="text-center">Payment Details</h5>
                                <p className="mb-0">
                                    <strong>Paid At:</strong>{' '}
                                    {invoice.paid_at
                                        ? moment(invoice.paid_at).format('YYYY-MM-DD HH:mm:ss')
                                        : '—'}
                                    <br />
                                    <strong>Payment Method:</strong> {invoice.paid_method || '—'}
                                    <br />
                                    <strong>Reference:</strong> {invoice.paid_reference || '—'}
                                    <br />
                                    <strong>Receipt #:</strong>{' '}
                                    {invoice.receipt?.receipt_number || '—'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-md-6">
                    <div className="card mb-3">
                        <div className="card-header">
                            <h5 className="mb-0 text-center">Totals</h5>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm table-striped text-center mb-0">
                                <tbody>
                                <tr>
                                    <td>Subtotal</td>
                                    <td>{invoice.subtotal}</td>
                                </tr>
                                <tr>
                                    <td>GST</td>
                                    <td>{invoice.tax_gst}</td>
                                </tr>
                                <tr>
                                    <td>PST</td>
                                    <td>{invoice.tax_pst}</td>
                                </tr>
                                <tr>
                                    <td>HST</td>
                                    <td>{invoice.tax_hst}</td>
                                </tr>
                                <tr>
                                    <td>QST</td>
                                    <td>{invoice.tax_qst}</td>
                                </tr>
                                <tr>
                                    <td>Discount</td>
                                    <td>{invoice.discount_amount}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <strong>Total</strong>
                                    </td>
                                    <td>
                                        <strong>
                                            {invoice.currency} {invoice.total}
                                        </strong>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-header">
                    <h5 className="mb-0 text-center">Invoice Items</h5>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-sm table-striped text-center mb-0">
                            <thead>
                            <tr>
                                <th>Delivery</th>
                                <th>Route</th>
                                <th>Completed</th>
                                <th>Driver</th>
                                <th>Subtotal</th>
                                <th>Tax</th>
                                <th>Discount</th>
                                <th>Total</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(invoice.items || []).map((item) => {
                                const pickup = item.pickup;
                                const meta = item.meta || {};
                                const route = `${meta.origin_address || ''} → ${
                                    meta.dropoff_address || ''
                                }`;
                                const taxTotal =
                                    parseFloat(item.tax_gst || 0) +
                                    parseFloat(item.tax_pst || 0) +
                                    parseFloat(item.tax_hst || 0) +
                                    parseFloat(item.tax_qst || 0);

                                return (
                                    <tr key={item.id}>
                                        <td>
                                            {pickup ? <Link to={`/pickups/${pickup.id}`}>#{pickup.id}</Link> : '—'}
                                        </td>
                                        <td>{route || item.description}</td>
                                        <td>
                                            {meta.completed_at
                                                ? moment(meta.completed_at).format('YYYY-MM-DD')
                                                : '—'}
                                        </td>
                                        <td>{meta.driver_name || pickup?.driver?.name || '—'}</td>
                                        <td>{item.line_subtotal}</td>
                                        <td>{taxTotal.toFixed(2)}</td>
                                        <td>{item.discount_amount}</td>
                                        <td>{item.line_total}</td>
                                    </tr>
                                );
                            })}

                            {(!invoice.items || invoice.items.length === 0) && (
                                <tr>
                                    <td colSpan="8">No invoice items found.</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withAuth(InvoiceDetails);
